// src/components/forms/AssessmentTaskForm.jsx (Updated with Task Levels)
import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  Upload, 
  X, 
  FileText, 
  Plus, 
  Minus, 
  School,
  Navigation,
  Building,
  Briefcase,
  MapPin,
  Users
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { ProjectService } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUniqueProvinces, 
  getDistrictsByProvince, 
  getSchoolsByFilters,
  toPhasePED
} from '../../datalists/Schools';
import SmartArrayInput from './SmartArrayInput';

const AssessmentTaskForm = ({ onSubmit, initialData = null }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    type: initialData?.type || 'Assignment',
    grade: initialData?.grade || '',
    subject: initialData?.subject || '',
    papers: initialData?.papers || [],
    topics: initialData?.topics || [''],
    totalMarks: initialData?.totalMarks || 0,
    taskFiles: initialData?.taskFiles || [],
    
    // New task level fields
    taskLevel: initialData?.taskLevel || 'school',
    schoolName: initialData?.schoolName || '',
    schoolCircuit: initialData?.schoolCircuit || '',
    schoolDistrict: initialData?.schoolDistrict || '',
    circuitName: initialData?.circuitName || '',
    districtName: initialData?.districtName || '',
    projectName: initialData?.projectName || '',
    projectSchools: initialData?.projectSchools || [''],
    
    questions: initialData?.questions || [{ 
      id: '',
      questionNumber: '1', 
      questionType: 'Short Answer',
      topic: '', 
      subtopic: '', 
      marks: 0, 
      taxonomyLevel: 'L1',
      tags: [''],
      concepts: [''],
      skills: [''],
      subquestions: []
    }]
  });

  const [newFiles, setNewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [subjectMetadata, setSubjectMetadata] = useState({
    tags: [],
    concepts: [],
    skills: [],
    topics: []
  });

  // Data for task level fields
  const [provinces] = useState(getUniqueProvinces());
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [registeredProjects, setRegisteredProjects] = useState([]);

  // Task levels
  const taskLevels = [
    { value: 'school', label: 'School Level', icon: School },
    { value: 'circuit', label: 'Circuit Level', icon: Navigation },
    { value: 'district', label: 'District Level', icon: Building },
    { value: 'project', label: 'Project Level', icon: Briefcase }
  ];

  // Question types
  const questionTypes = [
    'Multiple Choice',
    'True/False',
    'Missing Word',
    'Short Answer',
    'Calculation',
    'Graph Sketch',
    'Diagram Reading',
    'Essay'
  ];

  // Set user's grades and subjects when component mounts
  useEffect(() => {
    if (userProfile && !initialData) {
      setFormData(prev => ({
        ...prev,
        grade: userProfile.grades?.[0] || '',
        subject: userProfile.subjects?.[0] || '',
        schoolName: userProfile.schoolName || '',
        schoolCircuit: userProfile.circuit || '',
        schoolDistrict: userProfile.district || '',
        circuitName: userProfile.circuit || '',
        districtName: userProfile.district || ''
      }));
    }
  }, [userProfile, initialData]);

  // Load subject metadata when subject changes
  useEffect(() => {
    if (formData.subject) {
      loadSubjectMetadata();
    }
  }, [formData.subject]);

  // Load registered projects
  useEffect(() => {
    loadRegisteredProjects();
  }, []);

  // Update districts when task level is district or school and user's province is available
  useEffect(() => {
    if ((formData.taskLevel === 'district' || formData.taskLevel === 'school') && userProfile?.province) {
      const provinceDistricts = getDistrictsByProvince(userProfile.province);
      setDistricts(provinceDistricts);
    }
  }, [formData.taskLevel, userProfile?.province]);

  // Update schools when task level is school or project
  useEffect(() => {
    if ((formData.taskLevel === 'school' || formData.taskLevel === 'project') && 
        userProfile?.province && formData.schoolDistrict && userProfile?.schoolPhase) {
      const phasePED = toPhasePED(userProfile.schoolPhase);
      const filteredSchools = getSchoolsByFilters(
        phasePED,
        userProfile.province,
        formData.schoolDistrict
      );
      setSchools(filteredSchools);
    }
  }, [formData.taskLevel, formData.schoolDistrict, userProfile?.province, userProfile?.schoolPhase]);

  const loadSubjectMetadata = async () => {
    try {
      const metadata = await AssessmentService.getSubjectMetadata(formData.subject);
      setSubjectMetadata(metadata);
    } catch (error) {
      console.error('Error loading subject metadata:', error);
    }
  };

  const loadRegisteredProjects = async () => {
    try {
      const projects = await ProjectService.getUserProjects();
      setRegisteredProjects(projects.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Calculate total marks whenever questions change
  useEffect(() => {
    const total = formData.questions.reduce((sum, question) => {
      const questionTotal = question.subquestions.length > 0 
        ? question.subquestions.reduce((subSum, sub) => subSum + (sub.marks || 0), 0)
        : question.marks || 0;
      return sum + questionTotal;
    }, 0);
    
    setFormData(prev => ({ ...prev, totalMarks: total }));
  }, [formData.questions]);

  // Handle task level change
  const handleTaskLevelChange = (level) => {
    setFormData(prev => ({
      ...prev,
      taskLevel: level,
      // Reset level-specific fields
      schoolName: level === 'school' ? prev.schoolName : '',
      schoolCircuit: level === 'school' ? prev.schoolCircuit : '',
      schoolDistrict: level === 'school' ? prev.schoolDistrict : '',
      circuitName: level === 'circuit' ? prev.circuitName : '',
      districtName: (level === 'district' || level === 'circuit') ? prev.districtName : '',
      projectName: level === 'project' ? prev.projectName : '',
      projectSchools: level === 'project' ? prev.projectSchools : ['']
    }));
  };

  // Handle project schools management
  const handleAddProjectSchool = () => {
    setFormData(prev => ({
      ...prev,
      projectSchools: [...prev.projectSchools, '']
    }));
  };

  const handleRemoveProjectSchool = (index) => {
    setFormData(prev => ({
      ...prev,
      projectSchools: prev.projectSchools.filter((_, i) => i !== index)
    }));
  };

  const handleProjectSchoolChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      projectSchools: prev.projectSchools.map((school, i) => 
        i === index ? value : school
      )
    }));
  };

  // Topics management
  const handleAddTopic = () => {
    setFormData(prev => ({
      ...prev,
      topics: [...prev.topics, '']
    }));
  };

  const handleRemoveTopic = (index) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.filter((_, i) => i !== index)
    }));
  };

  const handleTopicChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.map((topic, i) => i === index ? value : topic)
    }));
  };

  // Question management
  const handleAddQuestion = () => {
    const newQuestion = {
      id: '',
      questionNumber: (formData.questions.length + 1).toString(),
      questionType: 'Short Answer',
      topic: '',
      subtopic: '',
      marks: 0,
      taxonomyLevel: 'L1',
      tags: [''],
      concepts: [''],
      skills: [''],
      subquestions: []
    };
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const handleRemoveQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, questionNumber: (i + 1).toString() }))
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  // File management
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const fileItems = files.map(file => ({
      file,
      name: file.name
    }));
    setNewFiles(prev => [...prev, ...fileItems]);
  };

  const handleRemoveFile = (index, isNew = true) => {
    if (isNew) {
      setNewFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      setFormData(prev => ({
        ...prev,
        taskFiles: prev.taskFiles?.filter((_, i) => i !== index) || []
      }));
    }
  };

  // Subquestion management
  const handleAddSubquestion = (questionIndex) => {
    const question = formData.questions[questionIndex];
    const newSubquestion = {
      questionNumber: `${question.questionNumber}.${question.subquestions.length + 1}`,
      questionType: 'Short Answer',
      topic: question.topic,
      subtopic: question.subtopic,
      marks: 0,
      taxonomyLevel: 'L1',
      tags: [''],
      concepts: [''],
      skills: ['']
    };

    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, subquestions: [...q.subquestions, newSubquestion] }
          : q
      )
    }));
  };

  const handleRemoveSubquestion = (questionIndex, subIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              subquestions: q.subquestions.filter((_, j) => j !== subIndex)
                .map((sub, index) => ({
                  ...sub,
                  questionNumber: `${q.questionNumber}.${index + 1}`
                }))
            }
          : q
      )
    }));
  };

  const handleSubquestionChange = (questionIndex, subIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? {
              ...q,
              subquestions: q.subquestions.map((sub, j) => 
                j === subIndex ? { ...sub, [field]: value } : sub
              )
            }
          : q
      )
    }));
  };

  // Smart array change handler
  const handleSmartArrayChange = (questionIndex, field, values, isSubquestion = false, subIndex = null) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex) {
          if (isSubquestion && subIndex !== null) {
            return {
              ...q,
              subquestions: q.subquestions.map((sub, j) => 
                j === subIndex ? { ...sub, [field]: values } : sub
              )
            };
          } else {
            return { ...q, [field]: values };
          }
        }
        return q;
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const taskData = {
        ...formData,
        topics: formData.topics.filter(t => t.trim() !== ''),
        projectSchools: formData.taskLevel === 'project' 
          ? formData.projectSchools.filter(s => s.trim() !== '')
          : [],
        questions: formData.questions.map(q => ({
          ...q,
          tags: q.tags?.filter(t => t.trim() !== '') || [],
          concepts: q.concepts?.filter(c => c.trim() !== '') || [],
          skills: q.skills?.filter(s => s.trim() !== '') || [],
          subquestions: q.subquestions.map(sub => ({
            ...sub,
            tags: sub.tags?.filter(t => t.trim() !== '') || [],
            concepts: sub.concepts?.filter(c => c.trim() !== '') || [],
            skills: sub.skills?.filter(s => s.trim() !== '') || []
          }))
        })),
        createdAt: initialData?.createdAt || new Date(),
        updatedAt: new Date()
      };

      let taskId;
      if (initialData?.id) {
        taskId = initialData.id;
        await AssessmentService.updateAssessmentTask(taskId, taskData);
      } else {
        taskId = await AssessmentService.createAssessmentTask(taskData);
      }

      // Upload new files
      const uploadedFiles = [...(initialData?.taskFiles || [])];
      for (const fileItem of newFiles) {
        if (fileItem.file && fileItem.name) {
          const url = await AssessmentService.uploadMedia(fileItem.file, userProfile.uid, taskId);
          uploadedFiles.push({
            name: fileItem.name,
            url: url,
            type: fileItem.file.type,
            size: fileItem.file.size
          });
        }
      }

      // Update task with files
      if (newFiles.length > 0) {
        await AssessmentService.updateAssessmentTask(taskId, { taskFiles: uploadedFiles });
      }

      onSubmit({ ...taskData, id: taskId, taskFiles: uploadedFiles });
    } catch (error) {
      console.error('Error saving assessment task:', error);
      alert('Error saving task. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Get task level icon
  const getTaskLevelIcon = (level) => {
    const levelConfig = taskLevels.find(tl => tl.value === level);
    return levelConfig ? levelConfig.icon : School;
  };

  return (
    <div className="max-w-5xl mx-auto p-4 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        {initialData?.id ? 'Edit Assessment Task' : 'Create Assessment Task'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Task Name, Type, and Marks Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Name/Title *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Assignment">Assignment</option>
              <option value="Project">Project</option>
              <option value="Investigation">Investigation</option>
              <option value="Test">Test</option>
              <option value="Exam">Exam</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Marks *
            </label>
            <input
              type="number"
              value={formData.totalMarks}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
              title="Auto-calculated from questions"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto-calculated from question mark allocations
            </p>
          </div>
        </div>

        {/* Grade, Subject, and Papers Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grade *
            </label>
            <select
              value={formData.grade}
              onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Grade</option>
              {userProfile?.grades?.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
            </label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Subject</option>
              {userProfile?.subjects?.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Papers (optional)
            </label>
            <input
              type="text"
              value={formData.papers.join(', ')}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                papers: e.target.value.split(',').map(p => p.trim()).filter(p => p)
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Paper 1, Paper 2, etc."
            />
          </div>
        </div>

        {/* Task Level Selection */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Task Level
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {taskLevels.map((level) => {
              const IconComponent = level.icon;
              return (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => handleTaskLevelChange(level.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.taskLevel === level.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <IconComponent className="w-6 h-6" />
                    <span className="text-sm font-medium">{level.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Conditional Task Level Fields */}
          {formData.taskLevel === 'school' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="md:col-span-3 font-medium text-blue-900 flex items-center gap-2 mb-2">
                <School className="w-4 h-4" />
                School Level Details
              </h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Name *
                </label>
                <select
                  value={formData.schoolName}
                  onChange={(e) => setFormData(prev => ({ ...prev, schoolName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select School</option>
                  {schools.map(school => (
                    <option key={school} value={school}>{school}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Circuit *
                </label>
                <input
                  type="text"
                  value={formData.schoolCircuit}
                  onChange={(e) => setFormData(prev => ({ ...prev, schoolCircuit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter circuit name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School District *
                </label>
                <select
                  value={formData.schoolDistrict}
                  onChange={(e) => setFormData(prev => ({ ...prev, schoolDistrict: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.taskLevel === 'circuit' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="md:col-span-2 font-medium text-green-900 flex items-center gap-2 mb-2">
                <Navigation className="w-4 h-4" />
                Circuit Level Details
              </h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Circuit Name *
                </label>
                <input
                  type="text"
                  value={formData.circuitName}
                  onChange={(e) => setFormData(prev => ({ ...prev, circuitName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter circuit name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District *
                </label>
                <select
                  value={formData.districtName}
                  onChange={(e) => setFormData(prev => ({ ...prev, districtName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.taskLevel === 'district' && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-900 flex items-center gap-2 mb-4">
                <Building className="w-4 h-4" />
                District Level Details
              </h4>
              
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District Name *
                </label>
                <select
                  value={formData.districtName}
                  onChange={(e) => setFormData(prev => ({ ...prev, districtName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.taskLevel === 'project' && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-900 flex items-center gap-2 mb-4">
                <Briefcase className="w-4 h-4" />
                Project Level Details
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <select
                    value={formData.projectName}
                    onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Project</option>
                    {registeredProjects.map(project => (
                      <option key={project.id} value={project.name}>{project.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Schools
                  </label>
                  <div className="space-y-2">
                    {formData.projectSchools.map((school, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          value={school}
                          onChange={(e) => handleProjectSchoolChange(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select School</option>
                          {schools.map(schoolOption => (
                            <option key={schoolOption} value={schoolOption}>{schoolOption}</option>
                          ))}
                        </select>
                        {formData.projectSchools.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveProjectSchool(index)}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddProjectSchool}
                      className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Add School
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Topics Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topics *
          </label>
          <div className="space-y-2">
            {formData.topics.map((topic, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => handleTopicChange(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter topic"
                  list={`topics-datalist-${index}`}
                />
                <datalist id={`topics-datalist-${index}`}>
                  {subjectMetadata.topics.map((existingTopic, i) => (
                    <option key={i} value={existingTopic} />
                  ))}
                </datalist>
                {formData.topics.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTopic(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddTopic}
              className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <PlusCircle className="w-4 h-4" />
              Add Topic
            </button>
          </div>
        </div>

        {/* Task Files Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Task Files
          </label>
          
          {/* Existing Files */}
          {formData.taskFiles?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Files:</h4>
              <div className="space-y-2">
                {formData.taskFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium">{file.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index, false)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Files */}
          {newFiles.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">New Files:</h4>
              <div className="space-y-2">
                {newFiles.map((fileItem, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">{fileItem.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(fileItem.file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index, true)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload Files
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              />
            </label>
            <span className="text-xs text-gray-500">
              Accepted: PDF, DOC, DOCX, JPG, PNG, GIF (max 10MB each)
            </span>
          </div>
        </div>

        {/* Questions Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
            <span className="text-sm text-gray-500">
              {formData.questions.length} question{formData.questions.length !== 1 ? 's' : ''} • {formData.totalMarks} marks total
            </span>
          </div>

          <div className="space-y-6">
            {formData.questions.map((question, qIndex) => (
              <div key={qIndex} className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    Question {question.questionNumber}
                  </h4>
                  {formData.questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveQuestion(qIndex)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Question Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Question Type
                    </label>
                    <select
                      value={question.questionType}
                      onChange={(e) => handleQuestionChange(qIndex, 'questionType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {questionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Topic
                    </label>
                    <input
                      type="text"
                      value={question.topic}
                      onChange={(e) => handleQuestionChange(qIndex, 'topic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      list={`question-topics-${qIndex}`}
                    />
                    <datalist id={`question-topics-${qIndex}`}>
                      {subjectMetadata.topics.map((topic, i) => (
                        <option key={i} value={topic} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Subtopic
                    </label>
                    <input
                      type="text"
                      value={question.subtopic || ''}
                      onChange={(e) => handleQuestionChange(qIndex, 'subtopic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Marks {question.subquestions.length > 0 && '(Total)'}
                    </label>
                    <input
                      type="number"
                      value={
                        question.subquestions.length > 0
                          ? question.subquestions.reduce((sum, sub) => sum + (sub.marks || 0), 0)
                          : question.marks
                      }
                      onChange={(e) =>
                        question.subquestions.length === 0 &&
                        handleQuestionChange(qIndex, 'marks', parseInt(e.target.value) || 0)
                      }
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
                        question.subquestions.length > 0 ? 'bg-gray-100' : ''
                      }`}
                      min="0"
                      readOnly={question.subquestions.length > 0}
                    />
                  </div>
                </div>

                {/* Difficulty Level - only when no subquestions */}
                {question.subquestions.length === 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Difficulty Level
                      </label>
                      <select
                        value={question.taxonomyLevel || 'L1'}
                        onChange={(e) => handleQuestionChange(qIndex, 'taxonomyLevel', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="L1">L1 - Remember</option>
                        <option value="L2">L2 - Understand</option>
                        <option value="L3">L3 - Apply</option>
                        <option value="L4">L4 - Analyze</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Question Smart Array Inputs - only if no subquestions */}
                {question.subquestions.length === 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <SmartArrayInput
                      label="Tags"
                      values={question.tags || []}
                      existingOptions={subjectMetadata.tags}
                      onChange={(values) => handleSmartArrayChange(qIndex, 'tags', values)}
                      placeholder="Enter or select tags..."
                    />
                    
                    <SmartArrayInput
                      label="Concepts"
                      values={question.concepts || []}
                      existingOptions={subjectMetadata.concepts}
                      onChange={(values) => handleSmartArrayChange(qIndex, 'concepts', values)}
                      placeholder="Enter or select concepts..."
                    />
                    
                    <SmartArrayInput
                      label="Skills"
                      values={question.skills || []}
                      existingOptions={subjectMetadata.skills}
                      onChange={(values) => handleSmartArrayChange(qIndex, 'skills', values)}
                      placeholder="Enter or select skills..."
                    />
                  </div>
                )}

                {/* Subquestions */}
                {question.subquestions.length > 0 && (
                  <div className="ml-4 mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Subquestions</h5>
                    <div className="space-y-4">
                      {question.subquestions.map((subquestion, subIndex) => (
                        <div key={subIndex} className="p-4 bg-white rounded border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h6 className="text-sm font-medium text-gray-700">
                              Question {subquestion.questionNumber}
                            </h6>
                            <button
                              type="button"
                              onClick={() => handleRemoveSubquestion(qIndex, subIndex)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Subquestion Basic Info */}
                          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                              <select
                                value={subquestion.questionType}
                                onChange={(e) => handleSubquestionChange(qIndex, subIndex, 'questionType', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                {questionTypes.map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Topic</label>
                              <input
                                type="text"
                                value={subquestion.topic}
                                onChange={(e) => handleSubquestionChange(qIndex, subIndex, 'topic', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Subtopic</label>
                              <input
                                type="text"
                                value={subquestion.subtopic}
                                onChange={(e) => handleSubquestionChange(qIndex, subIndex, 'subtopic', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
                              <select
                                value={subquestion.taxonomyLevel}
                                onChange={(e) => handleSubquestionChange(qIndex, subIndex, 'taxonomyLevel', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="L1">L1</option>
                                <option value="L2">L2</option>
                                <option value="L3">L3</option>
                                <option value="L4">L4</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Marks</label>
                              <input
                                type="number"
                                value={subquestion.marks}
                                onChange={(e) => handleSubquestionChange(qIndex, subIndex, 'marks', parseInt(e.target.value) || 0)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                min="0"
                              />
                            </div>
                          </div>

                          {/* Subquestion Smart Array Inputs */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <SmartArrayInput
                              label="Tags"
                              values={subquestion.tags || []}
                              existingOptions={subjectMetadata.tags}
                              onChange={(values) => handleSmartArrayChange(qIndex, 'tags', values, true, subIndex)}
                              placeholder="Enter or select tags..."
                              className="text-xs"
                            />
                            
                            <SmartArrayInput
                              label="Concepts"
                              values={subquestion.concepts || []}
                              existingOptions={subjectMetadata.concepts}
                              onChange={(values) => handleSmartArrayChange(qIndex, 'concepts', values, true, subIndex)}
                              placeholder="Enter or select concepts..."
                              className="text-xs"
                            />
                            
                            <SmartArrayInput
                              label="Skills"
                              values={subquestion.skills || []}
                              existingOptions={subjectMetadata.skills}
                              onChange={(values) => handleSmartArrayChange(qIndex, 'skills', values, true, subIndex)}
                              placeholder="Enter or select skills..."
                              className="text-xs"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Subquestion Button */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleAddSubquestion(qIndex)}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="w-3 h-3" />
                    Add Subquestion
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-start pt-4">
              <button
                type="button"
                onClick={handleAddQuestion}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={uploading}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {initialData?.id ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              initialData?.id ? 'Update Task' : 'Create Task'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssessmentTaskForm;